/* eslint-disable @typescript-eslint/no-unused-vars */
import { cx } from '@emotion/css';
import type { CSSProperties, FC, ReactNode } from 'react';
import { useContext, useMemo, useRef } from 'react';

import type { BackgroundColor } from '../../constants';
import { Alignment } from '../../constants';
import type { MotifScheme } from '../../motif';
import {
  getMotifSchemeForLegacyBackgroundColor,
  getMotifVariableValue,
  MotifComponent,
  p1Css,
  useMotifStyles,
} from '../../motif';
import type { BaseComponentProps, Buttons } from '../../types';
import {
  BlockBackgroundColorContext,
  dataSetToAttributes,
  getBackgroundClassName,
} from '../../utils';
import { hideWhenDesktopCss, hideWhenMobileCss } from '../../utils/styles';
import { useAnimations } from '../Animations';
import type { Animation } from '../Animations/types';
import {
  bodyContainerCss,
  bodyContainerMediaSideCss,
  bodyContainerStretchCss,
  bodyCss,
  bodyStyleCss,
  borderColorCssVar,
  borderCss,
  contentBaseCss,
  contentLayoutCss,
  contentLayoutDirectionCss,
  contentLayoutDirectionMobileCss,
  contentMediaContainerCss,
  contentScrollMarginCss,
  contentWrapperNoMobilePaddingStyle,
  contentWrapperStyle,
  ctaContainerCss,
  ctaInnerCss,
  displayNoneCss,
  headerCss,
  listIconStyleCss,
  listIconUrlVariable,
  mediaOrderCss,
  mediaOrderMobileCss,
  subtitleBaseCss,
  textAlignmentBaseCss,
  textAlignmentDesktopStyle,
  textAlignmentMobileStyle,
  titleCss,
  transparentBackgroundCss,
} from './Content.styled';
import { DisplayMode, Layout, MediaDirection, MediaDirectionMobile } from './types';

export interface ContentProps extends BaseComponentProps {
  body?: ReactNode;
  bodyAlignment?: Alignment;
  bodyAlignmentMobile?: Alignment;
  callsToAction?: Buttons;
  displayOn?: DisplayMode;
  layout?: Layout;
  mediaDirection?: MediaDirection;
  mediaDirectionMobile?: MediaDirectionMobile;
  subtitle?: ReactNode;
  title?: ReactNode;
  titleAlignment?: Alignment;
  titleAlignmentMobile?: Alignment;
  motifScheme?: MotifScheme;
  /** @deprecated Use motifScheme instead. */
  backgroundColor?: BackgroundColor;
  borderColor?: BackgroundColor | MotifScheme;
  anchorId?: string;
  animation?: Animation;
  isRTL?: boolean;
  bodyDataset?: DOMStringMap;
  titleDataset?: DOMStringMap;
  subtitleDataset?: DOMStringMap;
  /** @deprecated. Use `asset` instead. */
  media?: ReactNode;
  asset?: ReactNode;
  listIconUrl?: string;
}

/**
 * Classnames added to primary HTML elements.
 *
 * Allows consumers to customize styles for various use cases.
 */
export enum ContentElementClass {
  /** Classname added to container HTML element that renders the `body` prop. */
  BODY = 'sdsm-content-body',

  /** Classname added to container HTML element that renders the `callsToAction` prop. */
  CALLS_TO_ACTION = 'sdsm-content-cta',

  /** Classname added to container HTML element that renders the `subtitle` prop. */
  SUBTITLE = 'sdsm-content-subtitle',

  /** Classname added to container HTML element that renders the `title` prop. */
  TITLE = 'sdsm-content-title',
}

// TODO: when we fully deprecate old Content, update the props
/** Newer content component that is cleaner and has removed lots of rarely used functionality */
export const Content: FC<ContentProps> = ({
  className,
  body,
  bodyAlignment,
  bodyAlignmentMobile,
  callsToAction,
  displayOn = DisplayMode.ALL,
  layout = Layout.NORMAL,
  mediaDirection = MediaDirection.TOP,
  mediaDirectionMobile = MediaDirectionMobile.TOP,
  subtitle,
  title,
  titleAlignment,
  titleAlignmentMobile,
  motifScheme,
  backgroundColor: backgroundColorProp,
  borderColor,
  anchorId,
  animation,
  isRTL,
  bodyDataset,
  titleDataset,
  subtitleDataset,
  asset,
  style,
  listIconUrl,
}) => {
  useMotifStyles(MotifComponent.CONTENT);
  const contentRef = useRef(null);
  const animationCssProps = useAnimations(contentRef, animation);

  const dir = isRTL ? 'rtl' : undefined;

  const blockBackgroundColor = useContext(BlockBackgroundColorContext);

  // Disable background color if border color is set
  const contentTheme = motifScheme ?? backgroundColorProp;
  const contentBackgroundColor = borderColor ? undefined : contentTheme;
  const backgroundColor = borderColor ? undefined : contentTheme || blockBackgroundColor;

  // since undefined is a valid value, we need to check if both are undefined or both are defined
  // the helper function we use returns default if undefined, so if one one value is undefined and the other is not,
  // we need to just return false. if both are undefined, we return true because both map to the same default.
  const isSameBackgroundColorAsBlock =
    (blockBackgroundColor === undefined) !== (backgroundColor === undefined)
      ? false
      : getMotifSchemeForLegacyBackgroundColor(
          blockBackgroundColor as BackgroundColor | MotifScheme
        ) ===
        getMotifSchemeForLegacyBackgroundColor(backgroundColor as BackgroundColor | MotifScheme);

  // helper variables
  const hasHeader = !!(title || subtitle);
  const hasCallsToAction = !!(
    (callsToAction && !Array.isArray(callsToAction)) ||
    (Array.isArray(callsToAction) && callsToAction.length)
  );
  const hasBody = body || hasCallsToAction;
  const hasContent = !!(title || subtitle || hasBody);
  const hasMediaAndContent = !!asset && hasContent;

  // Styles for the main <section> container
  const bgClass = contentBackgroundColor ? getBackgroundClassName(backgroundColor) : undefined;

  // Add css
  const finalInlineStyle = useMemo(() => {
    if (!borderColor) return style;

    // Get background color CSS variable based on the selected border color
    const borderColorScheme = getMotifSchemeForLegacyBackgroundColor(borderColor);
    const borderColorVar = getMotifVariableValue(borderColorScheme, '--bg-color');

    const borderInlineStyle = borderColorVar ? { [borderColorCssVar]: borderColorVar } : undefined;

    return { ...style, ...borderInlineStyle };
  }, [style, borderColor]);

  // Remove padding if the block BackgroundColor is the same as the content BackgroundColor
  // Only apply if there is no border color set
  const contentBoxStyle =
    !borderColor && isSameBackgroundColorAsBlock
      ? contentWrapperNoMobilePaddingStyle[layout]
      : contentWrapperStyle[layout];

  const sectionClassName = cx(
    MotifComponent.CONTENT,
    // style for background color class name
    bgClass,
    // base css that is always applied
    contentBaseCss,
    // special padding css based on background color of content and its containing block
    contentBoxStyle,
    {
      // style to hide/show on certain devices based on DisplayMode
      [hideWhenMobileCss]: displayOn === DisplayMode.DESKTOP_ONLY,
      [hideWhenDesktopCss]: displayOn === DisplayMode.MOBILE_ONLY,
      [displayNoneCss]: displayOn === DisplayMode.NEITHER,
      // if the content has media and text, use proper grid layout
      [contentLayoutCss[layout] || '']: hasMediaAndContent,
      // if the content has media and text, set proper ordering on grid items for proper direction
      [contentLayoutDirectionCss[layout]?.[mediaDirection]]: hasMediaAndContent,
      [contentLayoutDirectionMobileCss[layout]?.[mediaDirectionMobile]]: hasMediaAndContent,
      // We use this to override the background color and set it to transparent when bg colors are the same
      // or when content bg color isn't set.
      [transparentBackgroundCss]: !contentBackgroundColor || isSameBackgroundColorAsBlock,
      [borderCss]: !!borderColor,
    },
    className
  );

  return (
    <section
      id={anchorId}
      style={animationCssProps ? { ...finalInlineStyle, ...animationCssProps } : finalInlineStyle}
      data-testid={MotifComponent.CONTENT}
      className={cx(sectionClassName, contentScrollMarginCss)}
      ref={contentRef}
      dir={dir}
    >
      {!!asset && (
        <div
          data-testid="sdsm-content-media-container"
          className={cx(
            contentMediaContainerCss,
            mediaOrderCss[mediaDirection],
            mediaOrderMobileCss[mediaDirectionMobile]
          )}
        >
          <div data-testid="sdsm-content-media" data-animation-id="content-media">
            {asset}
          </div>
        </div>
      )}
      {hasContent && (
        <div
          data-testid="sdsm-content-header-and-body"
          className={cx(bodyContainerCss, {
            [bodyContainerStretchCss]:
              mediaDirection === MediaDirection.TOP || mediaDirection === MediaDirection.BOTTOM,
            [bodyContainerMediaSideCss]:
              mediaDirection === MediaDirection.LEFT || mediaDirection === MediaDirection.RIGHT,
          })}
        >
          {hasHeader && (
            <ContentHeader
              title={title}
              titleAlignment={titleAlignment}
              titleAlignmentMobile={titleAlignmentMobile}
              titleDataset={titleDataset}
              subtitle={subtitle}
              subtitleDataset={subtitleDataset}
            />
          )}
          {hasBody && (
            <ContentBody
              body={body}
              bodyAlignment={bodyAlignment}
              bodyAlignmentMobile={bodyAlignmentMobile}
              bodyDataset={bodyDataset}
              callsToAction={callsToAction}
              listIconUrl={listIconUrl}
              hasCallsToAction={hasCallsToAction}
            />
          )}
        </div>
      )}
    </section>
  );
};

type ContentHeaderProps = Pick<
  ContentProps,
  | 'title'
  | 'titleAlignment'
  | 'titleAlignmentMobile'
  | 'titleDataset'
  | 'subtitle'
  | 'subtitleDataset'
>;

/** Helper component - renders the title and subtitle for the content. */
const ContentHeader: FC<ContentHeaderProps> = ({
  title,
  titleDataset,
  subtitle,
  subtitleDataset,
  titleAlignment = Alignment.Start,
  titleAlignmentMobile = Alignment.Start,
}) => {
  const titleAlignmentClassName = cx(
    textAlignmentBaseCss,
    textAlignmentDesktopStyle[titleAlignment],
    textAlignmentMobileStyle[titleAlignmentMobile]
  );

  return (
    <div data-testid="sdsm-content-header" className={cx(headerCss, titleAlignmentClassName)}>
      {title && (
        <h3
          className={cx(titleCss, titleAlignmentClassName, ContentElementClass.TITLE)}
          data-testid={ContentElementClass.TITLE}
          data-animation-id="content-title"
          {...dataSetToAttributes(titleDataset)}
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <div
          className={cx(p1Css, subtitleBaseCss, ContentElementClass.SUBTITLE)}
          data-testid={ContentElementClass.SUBTITLE}
          data-animation-id="content-subtitle"
          {...dataSetToAttributes(subtitleDataset)}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
};

interface ContentBodyProps
  extends Pick<
    ContentProps,
    | 'body'
    | 'bodyAlignment'
    | 'bodyAlignmentMobile'
    | 'bodyDataset'
    | 'callsToAction'
    | 'listIconUrl'
  > {
  hasHeader?: boolean;
  hasCallsToAction?: boolean;
}

/** Helper component - renders the Body and CTAs foor the content. */
const ContentBody: FC<ContentBodyProps> = ({
  body,
  bodyDataset,
  callsToAction,
  listIconUrl,
  hasCallsToAction,
  bodyAlignment = Alignment.Start,
  bodyAlignmentMobile = Alignment.Start,
}) => {
  const bodyAlignmentClassName = cx(
    textAlignmentBaseCss,
    textAlignmentDesktopStyle[bodyAlignment],
    textAlignmentMobileStyle[bodyAlignmentMobile]
  );

  // Set the list icon url as a CSS variable to allow for a dynamic list-style-image property
  const columnCountVariableStyles = listIconUrl
    ? ({
        [listIconUrlVariable]: `url("${listIconUrl}")`,
      } as CSSProperties)
    : undefined;

  return (
    <div
      data-testid={ContentElementClass.BODY}
      style={columnCountVariableStyles}
      className={cx(ContentElementClass.BODY, bodyCss)}
    >
      {body && (
        <div
          className={cx(bodyStyleCss, bodyAlignmentClassName, {
            [listIconStyleCss]: !!listIconUrl,
          })}
          data-testid="sdsm-content-body-content"
          data-animation-id="content-body"
          {...dataSetToAttributes(bodyDataset)}
        >
          {body}
        </div>
      )}

      {hasCallsToAction && (
        <div
          data-testid={ContentElementClass.CALLS_TO_ACTION}
          className={cx(ctaContainerCss, ContentElementClass.CALLS_TO_ACTION)}
        >
          <div className={cx(ctaInnerCss, bodyAlignmentClassName)} data-animation-id="content-cta">
            {callsToAction}
          </div>
        </div>
      )}
    </div>
  );
};
